<template>
  <Tab v-slot="{ selected }" as="template">
    <button
      :class="[
        'flex grow items-center justify-center gap-2 border-b-2 p-3 font-bold outline-hidden focus:outline-hidden',
        vertical && 'border-r-2 border-b-0',
        selected ? selectedClasses : unselectedClasses,
        customClass,
      ]"
      :disabled="disabled"
    >
      <!-- Icon slot or prop -->
      <TIcon
        v-if="icon"
        :name="icon"
        :class="[
          'h-4 w-4 transition-colors duration-200 ease-in-out sm:h-5 sm:w-5',
          iconClass,
        ]"
      />
      <slot v-if="!icon" name="icon" :selected="selected" />

      <!-- Label content -->
      <span
        :class="[
          'text-sm transition-colors duration-200 ease-in-out sm:text-base',
          labelClass,
        ]"
      >
        <slot :selected="selected">
          {{ label }}
        </slot>
      </span>

      <!-- Optional badge or additional content -->
      <slot name="badge" :selected="selected" />
    </button>
  </Tab>
</template>

<script setup lang="ts">
import { Tab } from '@headlessui/vue';
import TIcon from '../Icon/TIcon.vue';
import type { IconType } from '../Icon/icon';

type Props = {
  /** Tab label text */
  label?: string;
  /** Icon name to display */
  icon?: IconType;
  /** Whether the tab is disabled */
  disabled?: boolean;
  /** Whether tabs are displayed vertically */
  vertical?: boolean;
  /** Custom CSS classes for the tab button */
  customClass?: string;
  /** Custom CSS classes for the icon */
  iconClass?: string;
  /** Custom CSS classes for the label */
  labelClass?: string;
  /** Custom CSS classes for selected state */
  selectedClasses?: string;
  /** Custom CSS classes for unselected state */
  unselectedClasses?: string;
};

withDefaults(defineProps<Props>(), {
  label: '',
  icon: undefined,
  customClass: '',
  iconClass: '',
  labelClass: '',
  selectedClasses: 'border-primary-500 text-primary-500',
  unselectedClasses: 'border-neutral-200 text-neutral-600',
});
</script>

<style scoped>
/* Disabled tab animations */
button:disabled {
  transition: opacity 0.2s ease-in-out !important;
}
</style>
